import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  isWrapperToGameClientEventType,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES,
} from '@/features/event-handlers/wrapper-event-handlers/game-client-events/types';
import { useHandleWrapperToGameClientEvent } from '@/features/event-handlers/wrapper-event-handlers/game-client-events/use-handle-wrapper-game-client-event';
import { isWrapperToProviderEvent } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { useHandleWrapperProviderEventMapper } from '@/features/event-handlers/wrapper-event-handlers/provider-events/use-handle-wrapper-provider-event-mapper';
import { gameDataSelector } from '@/features/game/store/game-data';
import { gameContextSelector } from '@/features/game-context/store/game-context';
import { geolocationJwtSelector } from '@/features/geolocation/store/geolocation';
import { DdEventCategory } from '@/features/logging/logger';
import {
  logProviderMessages,
  logUntrustedWrapperMessage,
  wrapperMessagesInclusions,
} from '@/features/logging/message-event-logger';
import { isTrustedWrapperMessage } from '@/features/wrapper-proxy/validate-incoming-wrapper-message';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy.ts';

export const useEventListener = () => {
  const { provider } = useSelector(gameContextSelector);
  const gameData = useSelector(gameDataSelector);
  const geocomplyToken = useSelector(geolocationJwtSelector);
  const { wrapperEventHandler, providerEventHandler } =
    useHandleWrapperProviderEventMapper(provider);

  const { mapGameClientEvent } = useHandleWrapperToGameClientEvent();

  const eventHandler = useCallback(
    (event: MessageEvent) => {
      // Logs messages from wrappers (Launcher/ iOS); runs before trust check so
      // inclusion-gated wrapper lifecycle messages are auditable when rejected.
      logProviderMessages(event?.data, {
        name: 'WRAPPER_MESSAGE',
        useCase: DdEventCategory.WraperMessages,
        logType: 'info',
        inclusions: wrapperMessagesInclusions,
      });

      if (!isTrustedWrapperMessage(event)) {
        logUntrustedWrapperMessage(event);
        return;
      }

      if (
        event?.data?.type ===
        WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.REQUEST_DETACH_SESSION_SNAPSHOT
      ) {
        WrapperProxy.sendDetachSessionSnapshot({
          fgpGameData: gameData,
          geocomplyToken: geocomplyToken ?? undefined,
        });
        return;
      }

      if (isWrapperToProviderEvent(event?.data?.type)) {
        if (wrapperEventHandler) {
          wrapperEventHandler(event);
        }
      } else if (isWrapperToGameClientEventType(event?.data?.type)) {
        mapGameClientEvent(event);
      } else if (providerEventHandler) {
        providerEventHandler(event);
      }
    },
    [
      wrapperEventHandler,
      providerEventHandler,
      mapGameClientEvent,
      gameData,
      geocomplyToken,
    ],
  );

  const mapIosGeolocationEvent = useCallback((event) => {
    window.postMessage(
      {
        type: 'GL/GEOLOCATION_RESULT',
        payload: event.detail,
      },
      '*',
    );
  }, []);

  useEffect(() => {
    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [eventHandler]);

  useLayoutEffect(() => {
    window.addEventListener('geolocationResult', mapIosGeolocationEvent);

    return () =>
      window.removeEventListener('geolocationResult', mapIosGeolocationEvent);
  }, [mapIosGeolocationEvent]);
};
